/**
 * IncidentReportForm · v2.6.9 batch Q
 *
 * Shared form used by both the Driver standalone page (/incident-report) and
 * the Rider Dashboard "Incident" tab. The two callers differ only in the
 * `submitterType` prop (`"driver"` adds a required Driver-Kind dropdown and
 * the title reads "TAXIcab Incident Commander", `"rider"` hides the
 * dropdown and shows "Incident Report").
 *
 * The form uses the existing axios client (`api` for driver, `customerApi`
 * for rider) — passed in via the `apiClient` prop so the same component
 * can be reused without coupling to a specific auth flow.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle, Camera, FileVideo, X, Trash2, Link as LinkIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const DRIVER_KINDS = [
  "Vehicle Damage",
  "Unprofessional Customer Incident",
  "Intoxicated Customer Incident",
  "Incident requiring Law Enforcement",
  "Unable to complete ride",
];

const MAX_PHOTOS = 15;
const MAX_VIDEOS = 2;
const MAX_BYTES = 100 * 1024 * 1024; // 100 MB cap (per spec batch Q)
const MAX_WORDS = 5000;

function makePreviewIncidentNumber() {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  return `BTS-${today}-PREVIEW`;
}

export default function IncidentReportForm({
  submitterType,        // "driver" | "rider"
  submitterName,        // pre-filled, read-only
  apiClient,            // axios instance (api or customerApi)
  uploadEndpoint = "/uploads/incident-media",
  submitEndpoint,       // /driver/incidents | /customer/incidents
  onSubmitted,          // optional callback({incident})
}) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const nowHm = new Date().toTimeString().slice(0, 5);
  const [form, setForm] = useState({
    incident_date: todayIso,
    incident_time: nowHm,
    submitter_phone: "",
    submitter_address: "",
    other_party_phone: "",
    other_party_address: "",
    starting_address: "",
    ending_address: "",
    body: "",
    has_dashcam: false,
    driver_kind: "",
    police_officer_name: "",
    police_jurisdiction: "",
    police_case_number: "",
    external_video_url: "",
  });
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const fileInputRef = useRef(null);
  const previewIncidentNo = useMemo(() => makePreviewIncidentNumber(), []);

  const wordCount = useMemo(() => form.body.trim().split(/\s+/).filter(Boolean).length, [form.body]);
  const overWords = wordCount > MAX_WORDS;
  const photos = media.filter((m) => m.kind === "photo");
  const videos = media.filter((m) => m.kind === "video");

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const onFilePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    for (const f of files) {
      const isVideo = f.type.startsWith("video/");
      const isImage = f.type.startsWith("image/");
      if (!isImage && !isVideo) { toast.error(`${f.name} · only images or videos`); continue; }
      if (isVideo && videos.length >= MAX_VIDEOS) { toast.error(`Max ${MAX_VIDEOS} videos`); continue; }
      if (isImage && photos.length >= MAX_PHOTOS) { toast.error(`Max ${MAX_PHOTOS} photos`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name} exceeds 100 MB · use the URL field for larger clips`); continue; }
      try {
        setUploadingFile(f.name);
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await apiClient.post(uploadEndpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
        const base = process.env.REACT_APP_BACKEND_URL || "";
        setMedia((m) => [...m, {
          file_id: data.file_id,
          url: base + data.url,
          kind: data.kind,
          size: data.size,
          content_type: data.content_type,
          original_name: data.original_name || f.name,
        }]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `${f.name} · upload failed`);
      } finally { setUploadingFile(null); }
    }
  };

  const removeMedia = (file_id) => setMedia((m) => m.filter((x) => x.file_id !== file_id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.body.trim()) { toast.error("Incident narrative is required"); return; }
    if (overWords) { toast.error(`Body exceeds ${MAX_WORDS} words`); return; }
    if (submitterType === "driver" && !form.driver_kind) { toast.error("Please select an incident type"); return; }
    setBusy(true);
    try {
      const { data } = await apiClient.post(submitEndpoint, {
        ...form,
        media,
        external_video_url: form.external_video_url.trim() || null,
        // Driver-only field stripped on rider side.
        driver_kind: submitterType === "driver" ? form.driver_kind : null,
      });
      toast.success(`Submitted · ${data.incident.incident_number}`);
      if (typeof onSubmitted === "function") onSubmitted(data);
      // Reset narrative + media but keep contact info — useful when filing a follow-up.
      setForm((s) => ({ ...s, body: "", driver_kind: "", police_officer_name: "", police_jurisdiction: "", police_case_number: "", external_video_url: "" }));
      setMedia([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  const title = submitterType === "driver" ? "TAXIcab Incident Commander" : "Incident Report";
  const submitterFieldLabel = submitterType === "driver" ? "Driver's Name" : "Rider's Name";

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-5" data-testid={`${submitterType}-incident-form`}>
      <header className="flex items-start gap-3">
        <AlertTriangle className="h-6 w-6 text-amber-400 mt-1 flex-shrink-0"/>
        <div>
          <h2 className="text-xl font-extrabold tracking-tight">{title}</h2>
          <p className="text-[11px] text-zinc-500 mt-1">All fields submitted here are kept on record. Reports cannot be edited after submission — once you click <span className="text-amber-300 font-semibold">SAVE & SUBMIT</span>, dispatch reviews and routes them.</p>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldReadOnly testid="incident-number" label="Incident Number" value={previewIncidentNo} hint="Final number assigned on save"/>
        <FieldReadOnly testid="incident-submitter-name" label={submitterFieldLabel} value={submitterName || "—"}/>
        <Field testid="incident-date" label="Incident Date" type="date" value={form.incident_date} onChange={set("incident_date")} required/>
        <Field testid="incident-time" label="Incident Time" type="time" value={form.incident_time} onChange={set("incident_time")} required/>
      </div>

      <Section title="Submitter contact (so dispatch can reach you)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field testid="incident-self-phone" label="Phone Number" value={form.submitter_phone} onChange={set("submitter_phone")} placeholder="(580) 555-1234"/>
          <Field testid="incident-self-address" label="Address" value={form.submitter_address} onChange={set("submitter_address")} placeholder="Where you can be reached"/>
        </div>
      </Section>

      <Section title="Other party contact (optional)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field testid="incident-other-phone" label="Phone Number" value={form.other_party_phone} onChange={set("other_party_phone")} placeholder="If applicable"/>
          <Field testid="incident-other-address" label="Address" value={form.other_party_address} onChange={set("other_party_address")} placeholder="If applicable"/>
        </div>
      </Section>

      <Section title="Ride locations">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field testid="incident-start" label="Starting Address (where ride initiated)" value={form.starting_address} onChange={set("starting_address")}/>
          <Field testid="incident-end" label="Ending Address (drop-off · or where it should have been)" value={form.ending_address} onChange={set("ending_address")}/>
        </div>
      </Section>

      <Section title="Narrative">
        <textarea
          data-testid="incident-body"
          rows={10}
          value={form.body}
          onChange={set("body")}
          placeholder="Describe what happened in detail. Drag the bottom-right corner to expand."
          className="w-full bg-zinc-950 border border-zinc-700 rounded p-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-amber-400/60 focus:outline-none resize-y min-h-[180px]"
        />
        <div className={`text-[10px] mt-1 text-right ${overWords ? "text-red-400 font-bold" : "text-zinc-500"}`} data-testid="incident-word-count">
          {wordCount.toLocaleString()} / {MAX_WORDS.toLocaleString()} words {overWords ? "· over limit" : ""}
        </div>
      </Section>

      <Section title="Dashcam">
        <div className="flex gap-3" data-testid="incident-dashcam-radio">
          <RadioPill checked={form.has_dashcam === true} onClick={() => setForm((s) => ({ ...s, has_dashcam: true }))} label="Yes — verifiable footage" testid="dashcam-yes"/>
          <RadioPill checked={form.has_dashcam === false} onClick={() => setForm((s) => ({ ...s, has_dashcam: false }))} label="No" testid="dashcam-no"/>
        </div>
      </Section>

      {submitterType === "driver" && (
        <Section title="Incident type (driver classification)">
          <Select value={form.driver_kind} onValueChange={(v) => setForm((s) => ({ ...s, driver_kind: v }))}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700" data-testid="incident-driver-kind"><SelectValue placeholder="Select…"/></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
              {DRIVER_KINDS.map((k) => (
                <SelectItem key={k} value={k} className="text-xs" data-testid={`kind-${k.replace(/\s/g, "_")}`}>{k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Section>
      )}

      <Section title="Law enforcement (if applicable)">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field testid="incident-officer-name" label="Officer Name" value={form.police_officer_name} onChange={set("police_officer_name")}/>
          <Field testid="incident-jurisdiction" label="Jurisdiction" value={form.police_jurisdiction} onChange={set("police_jurisdiction")}/>
          <Field testid="incident-case-number" label="Case Number" value={form.police_case_number} onChange={set("police_case_number")}/>
        </div>
      </Section>

      <Section title={`Media · photos (${photos.length}/${MAX_PHOTOS}) · videos (${videos.length}/${MAX_VIDEOS})`}>
        <div className="flex items-center gap-2 mb-2">
          <Button type="button" onClick={() => fileInputRef.current?.click()} disabled={!!uploadingFile} variant="outline" className="bg-zinc-950 border-zinc-700" data-testid="incident-pick-media">
            {uploadingFile ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Uploading {uploadingFile}…</> : <><Camera className="h-4 w-4 mr-1"/>Add photos / videos</>}
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple onChange={onFilePicked} className="hidden" data-testid="incident-file-input"/>
        </div>
        <p className="text-[10px] text-zinc-500 mb-2">Max 100 MB per file. Use the URL field below for larger dashcam clips hosted on Dropbox / Google Drive / YouTube.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {media.map((m) => (
            <div key={m.file_id} className="relative border border-zinc-800 rounded overflow-hidden bg-zinc-950" data-testid={`incident-media-${m.file_id}`}>
              {m.kind === "photo" ? (
                <img src={m.url} alt={m.original_name || "evidence"} className="w-full h-24 object-cover"/>
              ) : (
                <div className="h-24 flex items-center justify-center text-zinc-500"><FileVideo className="h-6 w-6"/></div>
              )}
              <div className="p-1 text-[9px] text-zinc-400 truncate">{m.original_name}</div>
              <button type="button" onClick={() => removeMedia(m.file_id)} className="absolute top-1 right-1 bg-zinc-950/80 rounded p-0.5 hover:bg-red-500/40" data-testid={`incident-rm-${m.file_id}`}>
                <X className="h-3 w-3 text-red-300"/>
              </button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="External video link (optional)">
        <div className="flex items-center gap-2">
          <LinkIcon className="h-4 w-4 text-zinc-500"/>
          <Input
            data-testid="incident-external-url"
            value={form.external_video_url}
            onChange={set("external_video_url")}
            placeholder="https://drive.google.com/file/… or https://www.dropbox.com/…"
            className="bg-zinc-950 border-zinc-700 flex-1"
          />
        </div>
      </Section>

      <div className="flex items-center justify-end gap-3 pt-2 border-t border-zinc-800">
        <Button data-testid="incident-submit-btn" onClick={submit} disabled={busy || overWords} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold h-12 px-6">
          {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Submitting…</> : "SAVE & SUBMIT"}
        </Button>
      </div>
    </Card>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-2">{title}</div>
      {children}
    </div>
  );
}

function Field({ testid, label, type = "text", value, onChange, placeholder, required }) {
  return (
    <div>
      <Label className="text-xs">{label}{required && <span className="text-red-400 ml-1">*</span>}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={onChange} placeholder={placeholder} required={required} className="bg-zinc-950 border-zinc-700 mt-1"/>
    </div>
  );
}

function FieldReadOnly({ testid, label, value, hint }) {
  return (
    <div>
      <Label className="text-xs text-zinc-500">{label}</Label>
      <Input data-testid={testid} value={value} readOnly disabled className="bg-zinc-950/60 border-zinc-800 mt-1 text-zinc-400 cursor-not-allowed"/>
      {hint && <div className="text-[9px] text-zinc-600 mt-0.5">{hint}</div>}
    </div>
  );
}

function RadioPill({ checked, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 px-3 py-2 rounded border text-xs font-semibold transition ${
        checked ? "bg-amber-400/15 border-amber-400 text-amber-200" : "bg-zinc-950 border-zinc-700 text-zinc-400 hover:border-zinc-600"
      }`}
    >
      {label}
    </button>
  );
}
